import React, { useState, useEffect } from 'react';
import './Calculator.css';

// Importing icons from react-icons library
import { FaArchive, FaIdCard, FaAngleDown } from 'react-icons/fa';
import { FaShirt } from 'react-icons/fa6';
import { Heading } from '../../../components';

const CostCalculator = () => {
  // State for form data with initial empty values
  const [formData, setFormData] = useState({
    garmentType: '',        // Type of clothing item
    serviceType: '',         // Type of laundry service
    quantity: '',            // Number of items
    membershipStatus: '',    // Customer membership level
    stainRemoval: false,    // Additional service flag
    expressDelivery: false, // Additional service flag
    alterations: false      // Additional service flag
  });

  // State for calculated total cost
  const [totalCost, setTotalCost] = useState(0);

  // Handler for all input changes (text, select, checkbox)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Effect to recalculate cost whenever form data changes
  useEffect(() => {
    calculateCost();
  }, [formData]);

  // Cost calculation function
  const calculateCost = () => {
    // Destructure form data for easier access
    const { garmentType, serviceType, quantity, membershipStatus, stainRemoval, expressDelivery, alterations } = formData;

    // Return early if required fields are empty
    if (!garmentType || !serviceType || !quantity || !membershipStatus) {
      setTotalCost(0);
      return;
    }

    // Base prices for different garment types
    const basePrices = {
      shirt: 5,    // Price for shirts
      dress: 10,   // Price for dresses
      suit: 15,    // Price for suits
      pants: 7     // Price for pants
    };

    // Multipliers for different service types
    const serviceMultipliers = {
      washFold: 1,      // Standard wash & fold
      dryCleaning: 1.5, // More expensive dry cleaning
      ironing: 0.5      // Less expensive ironing only
    };

    // Discount percentages based on membership level
    const membershipDiscounts = {
      regular: 0,   // No discount for regular members
      silver: 0.1,  // 10% discount for silver members
      gold: 0.2     // 20% discount for gold members
    };

    // Calculate base cost (price × service multiplier × quantity)
    const quantityNum = parseInt(quantity);
    let cost = basePrices[garmentType] * serviceMultipliers[serviceType] * quantityNum;
    
    // Apply membership discount
    cost -= cost * membershipDiscounts[membershipStatus];

    // Add additional service charges if selected
    if (stainRemoval) cost += 5;       // $5 for stain removal
    if (expressDelivery) cost += 10;    // $10 for express delivery
    if (alterations) cost += 7;         // $7 for alterations

    // Update total cost state (rounded to 2 decimal places)
    setTotalCost(cost.toFixed(2));
  };

  return (
    <section className="cost-calculator custom-form">
      <div className="box-container">
        <div className="content">
          {/* Form element (note: no onSubmit since calculation happens on change) */}
          <form className="calculator-form dark-form" id="calculatorForm">

            {/* Form Heading Component */}
            <Heading title={"Cost Calculator"} subTitle={"Calculate Your Laundry Costs"} sideHeading linear />

            {/* Garment Type Selection */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="garmentType"><FaShirt /></label>
                  <select
                    className="select-box"
                    id="garmentType"
                    name="garmentType"
                    value={formData.garmentType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Garment Type</option>
                    <option value="shirt">Shirt</option>
                    <option value="dress">Dress</option>
                    <option value="suit">Suit</option>
                    <option value="pants">Pants</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className='icon' /></div>
                </div>
              </div>
            </div>

            {/* Service Type Selection */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="serviceType"><FaShirt /></label>
                  <select
                    className="select-box"
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Service Type</option>
                    <option value="washFold">Wash & Fold</option>
                    <option value="dryCleaning">Dry Cleaning</option>
                    <option value="ironing">Ironing</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className='icon' /></div>
                </div>
              </div>
            </div>

            {/* Quantity Input */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="quantity"><FaArchive /></label>
                <input
                  type="number"
                  name="quantity"
                  className="box"
                  id="quantity"
                  min="1"  // Minimum quantity of 1
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="Items Quantity"
                  required
                />
              </div>
            </div>

            {/* Membership Status Selection */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="membershipStatus"><FaIdCard /></label>
                  <select
                    className="select-box"
                    id="membershipStatus"
                    name="membershipStatus"
                    value={formData.membershipStatus}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Membership</option>
                    <option value="regular">Regular</option>
                    <option value="silver">Silver</option>
                    <option value="gold">Gold</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className='icon' /></div>
                </div>
              </div>
            </div>

            {/* Additional Services Checkboxes */}
            <div className="extra-services">
              {/* Stain Removal Option */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="stainRemoval-checkbox"
                  name="stainRemoval"
                  checked={formData.stainRemoval}
                  onChange={handleChange}
                />
                <label htmlFor="stainRemoval-checkbox">Stain Removal</label>
                <div className="checkbox-indicator"></div>
              </div>

              {/* Express Delivery Option */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="expressDelivery-checkbox"
                  name="expressDelivery"
                  checked={formData.expressDelivery}
                  onChange={handleChange}
                />
                <label htmlFor="expressDelivery-checkbox">Express Delivery</label>
                <div className="checkbox-indicator"></div>
              </div>

              {/* Alterations Option */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="alterations-checkbox"
                  name="alterations"
                  checked={formData.alterations}
                  onChange={handleChange}
                />
                <label htmlFor="alterations-checkbox">Alterations</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Total Cost Display */}
            <div className="cost-total">
              <h5>Total</h5>
              <span id="totalCost">${totalCost}</span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CostCalculator;